'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the ticket fixes the label column at 350px, a loading line is one text line tall (1lh), and the hidden scrollbar has no utility */

import classNames from 'classnames';
import type { ReactNode } from 'react';
import { COMPARE_COLUMN_WIDTH_CLASS, useScrollRail } from './ScrollSync';

/**
 * The scrolling half of a comparison row: while the columns exceed it, this rail moves on the offset
 * every other rail shares, so a value stays under its own plan's header. Once they fit, the columns
 * share its width and nothing scrolls, which is what makes one markup serve every width.
 *
 * Below 1280 the rail takes a line of its own under the label band. It must say so with a full
 * basis: with `flex-1` alone its basis is zero, a zero-width item fits at the end of the label's
 * line, and the rail never wraps — it collapses to nothing and the values overflow off-screen.
 */
export const CompareRail = ({
  children,
  className,
  ...rest
}: {
  children?: ReactNode;
  className?: string;
} & {
  [dataAttr: `data-${string}`]: string | number | boolean | undefined;
}) => {
  const ref = useScrollRail();

  return (
    <div
      ref={ref}
      className={classNames(
        'flex min-w-0 basis-full overflow-x-auto md:overflow-x-visible lg:flex-1 lg:basis-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

/**
 * One column of a rail: `flex: 1 0 160px`. While the columns exceed the rail they keep 160 and it
 * scrolls; once they fit they share its width equally, at every width, so nothing jumps at 768.
 */
export const compareColumnClass = (extra?: string) =>
  classNames(COMPARE_COLUMN_WIDTH_CLASS, 'shrink-0 grow', extra);

/** A loading line the height of the surrounding text, so the loaded page keeps the same height. */
export const Pulse = ({ className }: { className: string }) => (
  <span
    aria-hidden="true"
    className={classNames(
      'block h-[1lh] animate-pulse rounded-radius/button bg-fill/alternative-3',
      className,
    )}
  />
);

/** The label side of a row: a full-width band below 1280, the fixed left column from 1280. */
export const compareLabelClass = (extra?: string) =>
  classNames(
    'basis-full lg:w-[350px] lg:shrink-0 lg:grow-0 lg:basis-auto',
    extra,
  );
